import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LibraryManager, ResourceStatus, ResourceType } from 'libs.library_manager';
import { Badge, Icon, InlineNotification, Link as NucleusLink, ModalWindow, Select, Spinner, Table, Text, useLocalizeMessage, } from 'libs.nucleus';
import { ApiClientService, useApiClient } from 'libs.react_internal';
import { collectDatasetFieldNames, findMissingFields, parseResourceVersion } from '../../utils';
const ACTION_OPTIONS = [
    { label: 'Update', value: 'UPDATE' },
    { label: 'Keep current', value: 'KEEP' },
];
const willAutoCreateNewVersion = (status) => status === ResourceStatus.PUBLISHED || status === ResourceStatus.INREVIEW;
/**
 * SSP-3464: chart-builder counterpart of the dashboard ReviewVersionsModal.
 *
 * Shows the chart's dataset with its current version vs. the latest active
 * PUBLISHED version available in the Library, and lets the user choose to
 * Update or Keep current. Apply persists the new `cubeConfigId` via the
 * `onApply` callback (or simply closes when the user picks "Keep current").
 */
export const ReviewDatasetVersionModal = ({ isOpen, onClose, cubeConfigId, cubeConfigKey, chartStatus, chartVersion, chartFieldReferences, onApply, }) => {
    const translate = useLocalizeMessage();
    const libraryClient = useApiClient(ApiClientService.LIBRARY);
    const libraryClientRef = useRef(libraryClient);
    libraryClientRef.current = libraryClient;
    const [row, setRow] = useState(null);
    // Field names exposed by the new dataset version. Threaded into onApply so
    // the parent can scrub dead references from the chart settings rather than
    // blanket-wiping the whole config.
    const [latestAvailableFieldNames, setLatestAvailableFieldNames] = useState(() => new Set());
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const hasFetchedRef = useRef(false);
    useEffect(() => {
        if (!isOpen) {
            hasFetchedRef.current = false;
            setRow(null);
            return;
        }
        if (hasFetchedRef.current || !cubeConfigId || !cubeConfigKey) {
            return;
        }
        hasFetchedRef.current = true;
        const fetchVersionInfo = async () => {
            setIsLoading(true);
            try {
                const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
                const [currentDataset, response] = (await Promise.all([
                    libraryManager.getResourceById(cubeConfigId, ResourceType.CUBE_CONFIG),
                    libraryManager.getResources(ResourceType.CUBE_CONFIG, {
                        key: cubeConfigKey,
                        status: ResourceStatus.PUBLISHED,
                    }),
                ]));
                const candidates = (response?.data || []);
                const currentVersion = parseResourceVersion(currentDataset?.version);
                const latest = candidates
                    .filter((c) => c.active && c.id !== cubeConfigId && parseResourceVersion(c.version) > currentVersion)
                    .sort((a, b) => parseResourceVersion(b.version) - parseResourceVersion(a.version))[0];
                if (!latest) {
                    setRow(null);
                    return;
                }
                // The Library list endpoint doesn't include the full `data` payload
                // (dimensions / measures live there), so re-fetch the latest by id
                // to get the schema we need for the discrepancy check.
                const latestFull = (await libraryManager.getResourceById(latest.id, ResourceType.CUBE_CONFIG)) ??
                    latest;
                const name = currentDataset?.name || latestFull.name || latest.name;
                const description = currentDataset?.metadata?.description ||
                    latestFull.metadata?.description ||
                    latest.metadata?.description ||
                    '';
                const currentChartVersionNum = parseResourceVersion(chartVersion) || 1;
                const autoCreatesVersion = willAutoCreateNewVersion(chartStatus);
                // SSP-3464: detect chart-field references that aren't present in the
                // latest dataset schema so the modal can warn the user before they
                // accept the auto-update.
                const availableFieldNames = collectDatasetFieldNames(latestFull);
                setLatestAvailableFieldNames(availableFieldNames);
                const missingFieldsInLatest = findMissingFields(chartFieldReferences, availableFieldNames);
                setRow({
                    rowId: cubeConfigKey,
                    name,
                    description,
                    chartVersion: `v${currentChartVersionNum}`,
                    // PUBLISHED / IN_REVIEW charts get cloned with CREATE_VERSION on
                    // Apply, so preview the bumped chart version (current + 1) next to
                    // the current one.
                    chartVersionAfterUpdate: autoCreatesVersion ? `v${currentChartVersionNum + 1}` : undefined,
                    datasetCurrentVersion: `v${currentVersion || latest.version}`,
                    datasetLatestVersion: `v${latest.version}`,
                    latestResourceId: latest.id,
                    missingFieldsInLatest,
                    action: '',
                });
            }
            catch {
                setRow(null);
            }
            finally {
                setIsLoading(false);
            }
        };
        fetchVersionInfo();
        // chartStatus/chartVersion/chartFieldReferences are read once when the
        // modal opens; they don't change while the modal is open so they aren't
        // included in the dep array.
    }, [isOpen, cubeConfigId, cubeConfigKey]);
    const handleActionChange = useCallback((option) => {
        if (!option) {
            return;
        }
        setRow((prev) => (prev ? { ...prev, action: option.value } : prev));
    }, []);
    const handleApply = useCallback(async () => {
        if (!row) {
            onClose();
            return;
        }
        if (row.action !== 'UPDATE') {
            onClose();
            return;
        }
        setIsSaving(true);
        try {
            await onApply(row.latestResourceId, latestAvailableFieldNames);
            onClose();
        }
        finally {
            setIsSaving(false);
        }
    }, [row, latestAvailableFieldNames, onApply, onClose]);
    const actionOptions = useMemo(() => ACTION_OPTIONS.map((o) => ({ ...o, label: translate(o.label) })), [translate]);
    const columns = useMemo(() => [
        {
            header: translate('Name'),
            accessorKey: 'name',
            cell: ({ row: r }) => (_jsx(NucleusLink, { href: `/library/data-views/${r.original.latestResourceId}`, target: '_blank', children: _jsxs("span", { className: 'flex items-center gap-1', children: [r.original.name, _jsx(Icon, { name: 'externalLink', size: 'sm', color: 'current' })] }) })),
        },
        {
            header: translate('Description'),
            accessorKey: 'description',
            cell: ({ row: r }) => (_jsx(Text, { size: 'sm', className: 'truncate', children: r.original.description || '-' })),
        },
        {
            header: translate('Chart version'),
            accessorKey: 'chartVersion',
            cell: ({ row: r }) => {
                const { chartVersion: cv, chartVersionAfterUpdate, action } = r.original;
                // Only preview the bumped version once the user explicitly picks
                // "Update to latest". Keeping it hidden until then avoids implying
                // a state change the user hasn't confirmed.
                if (action !== 'UPDATE' || !chartVersionAfterUpdate) {
                    return _jsx(Badge, { variant: 'common', label: cv });
                }
                return (_jsxs("div", { className: 'flex items-center gap-1', children: [_jsx(Badge, { variant: 'common', label: cv }), _jsx("span", { className: 'text-text-secondary', children: "\u2192" }), _jsx(Badge, { variant: 'common', label: chartVersionAfterUpdate })] }));
            },
            size: 180,
        },
        {
            header: translate('Dataset current version'),
            accessorKey: 'datasetCurrentVersion',
            cell: ({ row: r }) => _jsx(Badge, { variant: 'common', label: r.original.datasetCurrentVersion }),
            size: 180,
        },
        {
            header: translate('Dataset latest version'),
            accessorKey: 'datasetLatestVersion',
            cell: ({ row: r }) => _jsx(Badge, { variant: 'common', label: r.original.datasetLatestVersion }),
            size: 180,
        },
        {
            header: '',
            accessorKey: 'action',
            cell: ({ row: r }) => {
                const selected = actionOptions.find((o) => o.value === r.original.action);
                return (_jsx(Select, { placeholder: translate('--Select--'), options: actionOptions, value: selected, onChange: handleActionChange, width: 'full' }));
            },
            size: 180,
        },
    ], [translate, actionOptions, handleActionChange]);
    const footerPrimaryButton = {
        label: translate('Apply'),
        onClick: handleApply,
        disabled: !row || row.action === '' || isLoading || isSaving,
        ...(isSaving && { content: _jsx(Spinner, { size: 'sm' }) }),
    };
    const footerSecondaryButtons = [
        {
            label: translate('Cancel'),
            onClick: onClose,
            disabled: isSaving,
        },
    ];
    if (!isOpen) {
        return null;
    }
    return (_jsx(ModalWindow, { title: translate('Review versions'), isOpen: isOpen, closeWindow: onClose, footerPrimaryActionButton: footerPrimaryButton, footerSecondaryActionButtons: footerSecondaryButtons, width: 'full', children: isLoading ? (_jsx("div", { className: 'flex items-center justify-center py-16', children: _jsx(Spinner, {}) })) : !row ? (_jsx("div", { className: 'flex items-center justify-center py-16', children: _jsx(Text, { className: 'text-text-secondary', children: translate('The dataset is up to date.') }) })) : (_jsxs("div", { className: 'flex flex-col gap-4', children: [row.action === 'UPDATE' && row.missingFieldsInLatest.length > 0 && (_jsx(InlineNotification, { type: 'warning', title: translate('Some chart fields will be removed'), subtitle: _jsxs("div", { className: 'flex flex-col gap-2', children: [_jsx(Text, { size: 'sm', children: translate("The fields below aren't in the new dataset version, so we'll remove them from this chart's settings when you apply. Review the chart and run a preview afterwards to make sure it still looks right.") }), _jsxs(Text, { size: 'sm', children: [_jsxs("strong", { children: [translate('Removed fields'), ":"] }), " ", row.missingFieldsInLatest.join(', ')] })] }) })), chartStatus === ResourceStatus.PUBLISHED && (_jsx(InlineNotification, { type: 'info', title: translate('A new version of this chart will be created'), subtitle: translate('This chart is in Ready status and cannot be modified directly. Applying updates will create a new version (In progress) and archive the current one.') })), _jsx(Table, { columns: columns, data: [row], getRowId: (r) => r.rowId, labelNoResults: translate('No datasets to review.') })] })) }));
};
